import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, Store, Ban, ClipboardCheck, ShieldCheck, AlertTriangle,
  Megaphone, Heart, MessageSquare, Sparkles, Activity, Flag,
} from "lucide-react";
import { useLang } from "@/context/LangContext";

import { Stat } from "./admin/Stat";
import UsersPanel from "./admin/UsersPanel";
import VendorsPanel from "./admin/VendorsPanel";
import AnalyticsPanel from "./admin/AnalyticsPanel";
import ReferralAnalytics from "./admin/ReferralAnalytics";
import MatrimonialApprovals from "./admin/MatrimonialApprovals";
import ReportsPanel from "./admin/ReportsPanel";
import AdsPanel from "./admin/AdsPanel";
import MaintenancePanel from "./admin/MaintenancePanel";
import SessionsPanel from "./admin/SessionsPanel";
import ImageReportsPanel from "./admin/ImageReportsPanel";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin console — thin shell that wires shared top-of-page analytics data
 * into feature-focused tabs. Each tab lives in `./admin/*` so this file
 * stays focused on layout, tab wiring, and shared data fetching.
 */
export default function AdminDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const [analytics, setAnalytics] = useState(null);
  const [tree, setTree] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, tr, r] = await Promise.all([
        api.get("/admin/analytics").then((x) => x.data),
        api.get("/admin/referral-tree").then((x) => x.data),
        api.get("/admin/reports").then((x) => x.data),
      ]);
      setAnalytics(a); setTree(tr); setReports(r);
    })();
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">{t("ad.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter">{t("ad.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("ad.subtitle")}</p>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label={t("ad.stat_total_users")} value={analytics.total_users} icon={Users} />
          <Stat label={t("ad.stat_approved_vendors")} value={analytics.total_vendors} icon={Store} />
          <Stat label={t("ad.stat_pending_vendors")} value={analytics.pending_vendors} icon={ClipboardCheck} />
          <Stat label={t("ad.stat_pending_users")} value={analytics.pending_users} icon={ShieldCheck} />
          <Stat label={t("ad.stat_blocked")} value={analytics.blocked_users} icon={Ban} />
          <Stat label={t("ad.stat_reports")} value={reports.length} icon={AlertTriangle} />
          <Stat label="Active inquiries" value={analytics.active_inquiries ?? 0} icon={Megaphone} />
          <Stat label="Total inquiries" value={analytics.total_inquiries ?? 0} icon={MessageSquare} />
          <Stat label="Inquiry replies" value={analytics.total_inquiry_replies ?? 0} icon={Sparkles} />
          <Stat label="Matrimonial profiles" value={analytics.total_matrimonial ?? 0} icon={Heart} />
          <Stat label="Self-listed matri." value={analytics.self_listed_matrimonial ?? 0} icon={Heart} />
          <Stat label="Matchmaker-listed" value={analytics.vendor_listed_matrimonial ?? 0} icon={Heart} />
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("ad.tab_users")}</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="admin-tab-vendors">{t("ad.tab_vendors")}</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">{t("ad.tab_analytics")}</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="admin-tab-referrals">{t("ad.tab_referrals")}</TabsTrigger>
          <TabsTrigger value="matrimonial" data-testid="admin-tab-matrimonial">Matrimonial</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">{t("ad.tab_reports")}</TabsTrigger>
          <TabsTrigger value="image-reports" data-testid="admin-tab-image-reports">
            <Flag className="h-3.5 w-3.5 mr-1" /> Image reports
          </TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads">{t("ad.tab_ads")}</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="admin-tab-maintenance">{t("ad.tab_maintenance")}</TabsTrigger>
          {isSuperadmin && (
            <TabsTrigger
              value="sessions"
              data-testid="admin-tab-sessions"
              className="relative data-[state=active]:bg-red-100 data-[state=active]:text-red-900"
            >
              <Activity className="h-3.5 w-3.5 mr-1" />
              Live sessions
              <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-red-700 dark:text-red-300">SA</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
        <TabsContent value="vendors" className="mt-4"><VendorsPanel /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsPanel analytics={analytics} /></TabsContent>
        <TabsContent value="referrals" className="mt-4"><ReferralAnalytics tree={tree} /></TabsContent>
        <TabsContent value="matrimonial" className="mt-4"><MatrimonialApprovals /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsPanel reports={reports} /></TabsContent>
        <TabsContent value="image-reports" className="mt-4"><ImageReportsPanel /></TabsContent>
        <TabsContent value="ads" className="mt-4"><AdsPanel /></TabsContent>
        <TabsContent value="maintenance" className="mt-4"><MaintenancePanel /></TabsContent>
        {isSuperadmin && (
          <TabsContent value="sessions" className="mt-4"><SessionsPanel /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
